/* eslint react-hooks/exhaustive-deps: "warn" */
import { useRef } from 'react';

import cn from 'classnames';

import { Link } from 'libs/router/Link';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './DefenceSecuritySupplyChain.module.scss';

type Tier = {
  n: string;
  title: string;
  sub: string;
  body: string;
};

type Translate = (key: string) => string;

const getTiers = (t: Translate): Tier[] => [
  {
    n: '01',
    title: t('The supplier you contracted'),
    sub: t('Named in the agreement'),
    body: t(
      'Legal entity verified against the register, ownership resolved to its ultimate parent, filing history and registered status confirmed.',
    ),
  },
  {
    n: '02',
    title: t('Their suppliers'),
    sub: t('No contract with you'),
    body: t(
      'Resolved from the tier-one group structure outward. Shared directors, shared addresses and common ownership surface links that no supplier declaration would show.',
    ),
  },
  {
    n: '03',
    title: t('Component and material sources'),
    sub: t('Usually invisible to the prime'),
    body: t(
      'Where sub-tier entities file, the chain continues. Where a jurisdiction does not publish, the gap is shown as a gap rather than filled with an estimate.',
    ),
  },
  {
    n: 'ALL',
    title: t('Ownership and control'),
    sub: t('Applied at every tier'),
    body: t(
      'Aggregate holdings tested against listing regimes at each level, so a restriction two tiers down is visible before it reaches a shipment.',
    ),
  },
];

export const DefenceSecuritySupplyChain = () => {
  const { t } = useInt();
  const tiers = getTiers(t);
  const railRef = useRef<HTMLDivElement>(null);
  const revealed = useIntersection(railRef, { threshold: 0.1 });

  return (
    <Section className={styles.root}>
      <div className={styles.head}>
        <div>
          <TitleBlock title={t('Your exposure sits three tiers below the contract you signed.')} align="left" />
        </div>
        <div>
          <p className={styles.lede}>
            {t(
              'Primes hold a supplier list. The risk sits in who owns the suppliers of those suppliers, in jurisdictions where the prime has no commercial relationship and no visibility. Registry filings are the only record that reaches that far down.',
            )}
          </p>
          <Link className={styles.link} route="/supply-chain-third-party-risk">
            {t('See supply chain risk')} &rarr;
          </Link>
        </div>
      </div>

      <div className={cn(styles.rail, revealed && styles.railIn)} ref={railRef}>
        {tiers.map((tier, i) => (
          <div className={styles.tier} style={{ transitionDelay: `${i * 70}ms` }} key={tier.n}>
            <div className={styles.tierLine}>
              <span className={styles.n}>{tier.n}</span>
            </div>
            <div className={styles.tierHead}>
              <h3 className={styles.title}>{tier.title}</h3>
              <span className={styles.sub}>{tier.sub}</span>
            </div>
            <p className={styles.body}>{tier.body}</p>
          </div>
        ))}
      </div>
    </Section>
  );
};
